import { BrowserClient, Hub, defaultIntegrations, makeMain } from '@sentry/browser';

import PaymentError from '@components/PaymentError/PaymentError';
import { DefaultConfig } from '@config/Config';
import { Environment, Nullable } from '@types-internal';

type SentryServiceState =
  | {
      status: 'UN_INITIALIZED';
    }
  | {
      status: 'INITIALIZED';
      hub: Hub;
    };

export type SentryError = {
  message?: string;
  retryable?: boolean;
  error?: unknown;
};

let sentryService: Nullable<SentryService> = null;

export function getSentryService(env: Environment, defaultConfig: DefaultConfig) {
  if (sentryService === null) {
    const { appVersion, sentryDsn } = defaultConfig;
    sentryService = new SentryService(sentryDsn, env, appVersion);
  } else {
    sentryService.maybeReset(env);
  }

  return sentryService;
}

export const captureException = (
  error: SentryError,
  captureContext?: (scope: any) => void,
): PaymentError => {
  return sentryService !== null
    ? sentryService.captureException(error, captureContext)
    : new PaymentError(error);
};

class SentryService {
  private env: Environment = 'dev';
  private readonly appVersion: string;
  private readonly dsn: string;
  private state: SentryServiceState = { status: 'UN_INITIALIZED' };
  private errorQueue: Error[] = [];

  constructor(dsn: string, env: Environment, appVersion: string) {
    this.env = env;
    this.dsn = dsn;
    this.appVersion = appVersion;
    this.initializeSentry();
  }

  private readonly initializeSentry = (whitelistUrls?: RegExp[]) => {
    try {
      const hub = new Hub(
        new BrowserClient({
          dsn: this.dsn,
          environment: this.env,
          integrations: [...defaultIntegrations],
          whitelistUrls,
        }),
      );
      hub.configureScope(scope => {
        scope.setTags({ version: this.appVersion });
      });
      makeMain(hub);
      this.state = {
        status: 'INITIALIZED',
        hub,
      };
    } catch (error) {
      this.errorQueue.push(error as Error);
    }
  };

  private readonly processErrorQueue = (hub: Hub) => {
    for (const error of this.errorQueue) {
      hub.captureException(error);
    }
    this.errorQueue = [];
  };

  public captureError(hub: Hub, sentryError: SentryError, captureContext?: (scope: any) => void) {
    if (sentryError.error) {
      hub.configureScope(scope => {
        if (captureContext) {
          captureContext(scope);
        }
      });
      hub.captureException(sentryError.error);
    } else {
      const paymentError = new PaymentError(sentryError);
      hub.configureScope(scope => {
        if (captureContext) {
          captureContext(scope);
        }
      });
      hub.captureException(paymentError);
    }
  }

  public captureException = (
    sentryError: SentryError,
    captureContext?: (scope: any) => void,
  ): PaymentError => {
    if (this.state.status === 'UN_INITIALIZED') {
      this.initializeSentry();
    }

    const isNotProdEnv = this.env !== 'prod';
    const paymentError = new PaymentError(sentryError);

    if (this.state.status === 'INITIALIZED') {
      this.processErrorQueue(this.state.hub);
      this.captureError(this.state.hub, sentryError, captureContext);

      if (isNotProdEnv) {
        const errorToLog = sentryError.error || paymentError;
        console.error(errorToLog);
      }
    } else if (sentryError.error) {
      // If not initialized, add to error queue
      this.errorQueue.push(sentryError.error as Error);
    } else {
      this.errorQueue.push(paymentError);
    }

    return paymentError;
  };

  public maybeReset(env: Environment) {
    if (this.env !== env) {
      const oldEnv = this.env;
      this.env = env;
      this.initializeSentry();
      this.captureException(new Error(`SentryService switch from environment ${oldEnv} to ${env}`));
    }
  }
}

export default SentryService;
